import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link, useSearchParams, useNavigate } from 'react-router'
import { useState, useCallback, useTransition } from 'react'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SearchAutocomplete } from '~/components/search-autocomplete'
import { InlineQuiz } from '~/components/inline-quiz'
import { ProMap } from '~/components/pro-map'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles } from '@afficheservices/db/schema'
import { ilike, eq, gte, sql, and } from 'drizzle-orm'
import {
  Star, CheckCircle, Clock, MapPin, ArrowRight, Search,
  SlidersHorizontal, Shield, X, Users,
  Wrench, Loader2, ChevronLeft, ChevronRight, Map, List, Sparkles,
} from 'lucide-react'
import { DYNAMIC_PAGE_HEADERS } from '~/lib/cache.server'
import { formatHours, formatRating } from '~/lib/format'

export function headers() { return DYNAMIC_PAGE_HEADERS }

// ─── Constants ───────────────────────────────────────────────

const PAGE_SIZE = 12

const RATING_OPTIONS = [
  { value: '', label: 'Toutes les notes' },
  { value: '3.5', label: '3.5+ étoiles' },
  { value: '4', label: '4+ étoiles' },
  { value: '4.5', label: '4.5+ étoiles' },
]

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Pertinence' },
  { value: 'rating', label: 'Meilleure note' },
  { value: 'reviews', label: 'Plus d\'avis' },
  { value: 'response_time', label: 'Temps de réponse' },
]

const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Toute expérience' },
  { value: '2', label: '2+ ans' },
  { value: '5', label: '5+ ans' },
  { value: '10', label: '10+ ans' },
]

// ─── Loader ──────────────────────────────────────────────────

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const tags: ReturnType<MetaFunction<typeof loader>> = [
    { title: `Recherche "${data?.query ?? ''}" — AfficheServices` },
    { name: 'description', content: `Résultats de recherche pour "${data?.query ?? ''}" — Trouvez les meilleurs professionnels près de chez vous.` },
  ]
  // rel=canonical + prev/next so Google consolidates paginated variants
  // (audit #23). Canonical strips ?page= so all pages roll up to page 1.
  if (data?.seo?.canonical) {
    tags.push({ tagName: 'link', rel: 'canonical', href: data.seo.canonical })
  }
  if (data?.seo?.prevUrl) {
    tags.push({ tagName: 'link', rel: 'prev', href: data.seo.prevUrl })
  }
  if (data?.seo?.nextUrl) {
    tags.push({ tagName: 'link', rel: 'next', href: data.seo.nextUrl })
  }
  // Filtered or paginated subviews shouldn't compete with the canonical
  // page in SERPs — emit noindex,follow on anything beyond page 1.
  if ((data?.pagination?.page ?? 1) > 1) {
    tags.push({ name: 'robots', content: 'noindex, follow' })
  }
  return tags
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const query = url.searchParams.get('q')?.trim() ?? ''
  const ratingFilter = url.searchParams.get('rating') ?? ''
  const cityFilter = url.searchParams.get('city') ?? ''
  const sortFilter = url.searchParams.get('sort') ?? 'relevance'
  const experienceFilter = url.searchParams.get('experience') ?? ''
  const verifiedOnly = url.searchParams.get('verified') === '1'
  const categoryFilter = url.searchParams.get('category') ?? ''
  const page = Math.max(1, parseInt(url.searchParams.get('page') ?? '1', 10))

  // Fetch distinct cities for the filter dropdown
  const citiesResult = await db
    .selectDistinct({ city: professionalProfiles.city })
    .from(professionalProfiles)
    .where(sql`${professionalProfiles.city} IS NOT NULL AND ${professionalProfiles.city} != ''`)
    .orderBy(professionalProfiles.city)

  const cities = citiesResult.map((c) => c.city).filter(Boolean)

  if (query.length < 2) {
    return {
      query, categories: [], pros: [], cities, allCategories: [],
      filters: { rating: ratingFilter, city: cityFilter, sort: sortFilter, experience: experienceFilter, verified: verifiedOnly },
      pagination: { page: 1, totalPages: 1, total: 0 },
      seo: { canonical: 'https://www.afficheservices.fr/recherche', prevUrl: null, nextUrl: null },
    }
  }

  const pattern = `%${query}%`

  // Matching categories
  const categories = await db
    .select({
      id: serviceCategories.id,
      name: serviceCategories.nameFr,
      slug: serviceCategories.slug,
      parentId: serviceCategories.parentId,
      leadPriceMin: serviceCategories.leadPriceMin,
      leadPriceMax: serviceCategories.leadPriceMax,
    })
    .from(serviceCategories)
    .where(ilike(serviceCategories.nameFr, pattern))
    .limit(10)

  // Build WHERE conditions for professionals
  const conditions = [
    sql`(${professionalProfiles.companyName} ILIKE ${pattern} OR ${professionalProfiles.description} ILIKE ${pattern} OR ${professionalProfiles.city} ILIKE ${pattern})`,
  ]

  if (ratingFilter) {
    conditions.push(gte(professionalProfiles.ratingAvg, ratingFilter))
  }

  if (cityFilter) {
    conditions.push(eq(professionalProfiles.city, cityFilter))
  }

  if (experienceFilter) {
    conditions.push(gte(professionalProfiles.yearsExperience, parseInt(experienceFilter, 10)))
  }

  if (verifiedOnly) {
    conditions.push(eq(professionalProfiles.identityVerified, true))
  }

  if (categoryFilter) {
    conditions.push(
      sql`${professionalProfiles.id} IN (
        SELECT pc.professional_id FROM professional_categories pc WHERE pc.category_id = ${categoryFilter}
      )`,
    )
  }

  // Fetch all categories for filter dropdown
  const allCategories = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr })
    .from(serviceCategories)
    .where(sql`${serviceCategories.parentId} IS NOT NULL`)
    .orderBy(serviceCategories.nameFr)
    .limit(50)

  // Count total
  const [{ count: totalCount }] = await db
    .select({ count: sql<number>`count(*)::int` })
    .from(professionalProfiles)
    .where(and(...conditions))

  // Build ORDER BY
  let orderBy
  switch (sortFilter) {
    case 'rating':
      orderBy = sql`${professionalProfiles.ratingAvg} DESC NULLS LAST`
      break
    case 'reviews':
      orderBy = sql`${professionalProfiles.reviewCount} DESC NULLS LAST`
      break
    case 'response_time':
      orderBy = sql`${professionalProfiles.responseTimeAvgHours} ASC NULLS LAST`
      break
    default:
      orderBy = sql`${professionalProfiles.ratingAvg} DESC NULLS LAST`
      break
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const offset = (Math.min(page, totalPages) - 1) * PAGE_SIZE

  const pros = await db
    .select({
      id: professionalProfiles.id,
      companyName: professionalProfiles.companyName,
      slug: professionalProfiles.slug,
      city: professionalProfiles.city,
      departmentCode: professionalProfiles.departmentCode,
      ratingAvg: professionalProfiles.ratingAvg,
      reviewCount: professionalProfiles.reviewCount,
      responseTimeAvgHours: professionalProfiles.responseTimeAvgHours,
      identityVerified: professionalProfiles.identityVerified,
      insuranceVerified: professionalProfiles.insuranceVerified,
      shortDescription: professionalProfiles.shortDescription,
      yearsExperience: professionalProfiles.yearsExperience,
      employeeCount: professionalProfiles.employeeCount,
      logoUrl: professionalProfiles.logoUrl,
      latitude: professionalProfiles.latitude,
      longitude: professionalProfiles.longitude,
    })
    .from(professionalProfiles)
    .where(and(...conditions))
    .orderBy(orderBy)
    .limit(PAGE_SIZE)
    .offset(offset)

  // Build canonical URL + prev/next URLs server-side so meta() can emit them
  // (audit #23). Strip the page param from the canonical so all paginated
  // variants consolidate authority on page 1.
  const baseUrl = new URL(request.url)
  const cleanParams = new URLSearchParams(baseUrl.searchParams)
  cleanParams.delete('page')
  const canonicalQs = cleanParams.toString()
  const canonical = `https://www.afficheservices.fr/recherche${canonicalQs ? '?' + canonicalQs : ''}`
  const buildPageUrl = (n: number) => {
    const p = new URLSearchParams(cleanParams)
    if (n > 1) p.set('page', String(n))
    const qs = p.toString()
    return `https://www.afficheservices.fr/recherche${qs ? '?' + qs : ''}`
  }
  const prevUrl = page > 1 ? buildPageUrl(page - 1) : null
  const nextUrl = page < totalPages ? buildPageUrl(page + 1) : null

  return {
    query,
    categories,
    pros,
    cities,
    allCategories,
    filters: { rating: ratingFilter, city: cityFilter, sort: sortFilter, experience: experienceFilter, verified: verifiedOnly, category: categoryFilter },
    pagination: { page, totalPages, total: totalCount },
    seo: { canonical, prevUrl, nextUrl },
  }
}

// ─── Filter Chip ─────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onRemove,
}: {
  label: string
  active: boolean
  onRemove: () => void
}) {
  if (!active) return null
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
      {label}
      <button type="button" onClick={onRemove} className="rounded-full p-0.5 hover:bg-primary/20">
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

// ─── Component ───────────────────────────────────────────────

export default function SearchResults() {
  const { query, categories, pros, cities, allCategories, filters, pagination } = useLoaderData<typeof loader>()
  const [searchParams, setSearchParams] = useSearchParams()
  const _navigate = useNavigate()
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [isPending, startTransition] = useTransition()
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set())

  const toggleCompare = useCallback((id: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCompareIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else if (next.size < 3) { next.add(id) }
      return next
    })
  }, [])

  const comparePros = pros.filter((p) => compareIds.has(p.id))

  const activeFilterCount = [
    filters.rating,
    filters.city,
    filters.experience,
    filters.verified,
    filters.category,
    filters.sort !== 'relevance' ? filters.sort : '',
  ].filter(Boolean).length

  // ── Update filter (instant, no form submit) ────────────────

  const updateFilter = useCallback(
    (key: string, value: string) => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams)
        if (value) {
          params.set(key, value)
        } else {
          params.delete(key)
        }
        // Reset to page 1 when filters change
        params.delete('page')
        setSearchParams(params, { replace: true })
      })
    },
    [searchParams, setSearchParams],
  )

  const clearAllFilters = useCallback(() => {
    startTransition(() => {
      const params = new URLSearchParams()
      params.set('q', query)
      setSearchParams(params, { replace: true })
    })
  }, [query, setSearchParams])

  const goToPage = useCallback(
    (page: number) => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams)
        if (page > 1) {
          params.set('page', String(page))
        } else {
          params.delete('page')
        }
        setSearchParams(params, { replace: true })
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [searchParams, setSearchParams],
  )

  // ── Filter sidebar content (reused for mobile sheet) ───────

  function FilterControls({ className = '' }: { className?: string }) {
    return (
      <div className={className}>
        {/* Rating */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Note minimum</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {RATING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateFilter('rating', opt.value === filters.rating ? '' : opt.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                  opt.value === filters.rating
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {opt.value && <Star className="mr-1 inline h-3 w-3 fill-amber-400 text-amber-400" />}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* City */}
        <div className="mt-5">
          <label htmlFor="filter-city" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ville</label>
          <select
            id="filter-city"
            value={filters.city}
            onChange={(e) => updateFilter('city', e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Toutes les villes</option>
            {cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="mt-5">
          <label htmlFor="filter-category" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Catégorie</label>
          <select
            id="filter-category"
            value={filters.category}
            onChange={(e) => updateFilter('category', e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Toutes les catégories</option>
            {allCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Experience */}
        <div className="mt-5">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Expérience</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {EXPERIENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateFilter('experience', opt.value === filters.experience ? '' : opt.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                  opt.value === filters.experience
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Verified only */}
        <div className="mt-5">
          <button
            type="button"
            onClick={() => updateFilter('verified', filters.verified ? '' : '1')}
            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all ${
              filters.verified
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <Shield className="h-4 w-4" />
            Identité vérifiée uniquement
          </button>
        </div>

        {/* Clear all */}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="mt-5 w-full rounded-lg border border-border px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50"
          >
            Effacer tous les filtres ({activeFilterCount})
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      {/* ── Search bar (compact, sticky) ──────────────── */}
      <div className="sticky top-0 z-30 border-b border-border bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-3 lg:px-6">
          <SearchAutocomplete variant="compact" defaultQuery={query} />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 lg:px-6">
        {/* ── Header ──────────────────────────────────── */}
        {/* Popular searches — shown when query is short */}
        {query.length < 2 && (
          <div className="mb-8">
            <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Rechercher un professionnel</h1>
            <p className="mt-2 text-sm text-slate-500">Tapez au moins 2 caractères ou essayez une recherche populaire</p>
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Recherches populaires</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {['Plombier Paris', 'Électricien', 'Peinture appartement', 'Rénovation salle de bain', 'Serrurier urgence', 'Couvreur toiture'].map((q) => (
                  <Link key={q} to={`/recherche?q=${encodeURIComponent(q)}`} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 transition-all hover:border-primary hover:bg-primary-50 hover:text-primary">
                    {q}
                  </Link>
                ))}
              </div>
            </div>
            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Parcourir par catégorie</p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {['plomberie', 'electricite', 'peinture', 'renovation', 'menuiserie', 'toiture', 'serrurerie', 'climatisation'].map((slug) => (
                  <Link key={slug} to={`/services/${slug}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-medium text-slate-700 transition-all hover:border-primary hover:shadow-sm">
                    {slug.charAt(0).toUpperCase() + slug.slice(1)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {query.length >= 2 && <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
              Résultats pour &ldquo;{query}&rdquo;
            </h1>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">
              {pagination.total} résultat{pagination.total > 1 ? 's' : ''}
            </span>
            {isPending && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          </div>

          {/* View toggle + Mobile filter toggle */}
          <div className="flex items-center gap-2">
            {/* List / Map toggle — mobile only (desktop shows split-view) */}
            <div className="flex items-center rounded-lg border border-border bg-white lg:hidden">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 rounded-l-lg px-3 py-2 text-xs font-medium transition-all ${
                  viewMode === 'list' ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <List className="h-3.5 w-3.5" /> Liste
              </button>
              <button
                type="button"
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-1.5 rounded-r-lg px-3 py-2 text-xs font-medium transition-all ${
                  viewMode === 'map' ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Map className="h-3.5 w-3.5" /> Carte
              </button>
            </div>

          {/* Mobile filter toggle */}
          <button
            type="button"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 lg:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtres
            {activeFilterCount > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          </div>
        </div>}

        {/* ── Active filter chips ─────────────────────── */}
        {activeFilterCount > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <FilterChip
              label={`Note ${filters.rating}+`}
              active={!!filters.rating}
              onRemove={() => updateFilter('rating', '')}
            />
            <FilterChip
              label={filters.city}
              active={!!filters.city}
              onRemove={() => updateFilter('city', '')}
            />
            <FilterChip
              label={`${filters.experience}+ ans exp.`}
              active={!!filters.experience}
              onRemove={() => updateFilter('experience', '')}
            />
            <FilterChip
              label="Vérifié"
              active={filters.verified}
              onRemove={() => updateFilter('verified', '')}
            />
          </div>
        )}

        {/* ── Sort bar ────────────────────────────────── */}
        <div className="mt-4 flex items-center gap-3">
          <span className="text-xs text-slate-400">Trier par :</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateFilter('sort', opt.value)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                filters.sort === opt.value
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* ── Main layout ─────────────────────────────── */}
        <div className="mt-6 flex gap-6">
          {/* Sidebar filters (desktop) */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-20 rounded-2xl border border-border bg-white p-5 shadow-xs">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <SlidersHorizontal className="h-4 w-4 text-slate-400" />
                Filtres
              </h3>
              <FilterControls className="mt-4" />
            </div>
          </aside>

          {/* Mobile filter sheet */}
          {showMobileFilters && (
            <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Filtres">
              <button
                type="button"
                aria-label="Fermer les filtres"
                onClick={() => setShowMobileFilters(false)}
                className="absolute inset-0 cursor-default bg-black/40"
              />
              <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white p-6 animate-slide-in">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">Filtres</h3>
                  <button type="button" onClick={() => setShowMobileFilters(false)} className="rounded-full p-2 hover:bg-slate-100">
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>
                <FilterControls className="mt-4" />
                <button
                  type="button"
                  onClick={() => setShowMobileFilters(false)}
                  className="mt-6 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press"
                >
                  Voir {pagination.total} résultat{pagination.total > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <div className="min-w-0 flex-1">
            {/* Categories */}
            {categories.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Catégories correspondantes</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/services/${cat.slug}`}
                      className="card-hover flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm shadow-xs"
                    >
                      <Wrench className="h-4 w-4 text-primary" />
                      <div>
                        <span className="font-medium text-slate-900">{cat.name}</span>
                        {cat.leadPriceMin && (
                          <span className="ml-2 text-xs text-slate-400">
                            dès {cat.leadPriceMin} €
                          </span>
                        )}
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Map view — mobile only (desktop map is in the sticky sidebar) */}
            {viewMode === 'map' && pros.length > 0 && (
              <div className="rounded-2xl border border-border bg-white shadow-xs overflow-hidden lg:hidden">
                <ProMap
                  pros={pros}
                  centerLat={(() => {
                    const c = pros.filter(p => p.latitude && p.longitude)
                    return c.length > 0 ? c.reduce((s, p) => s + parseFloat(p.latitude!), 0) / c.length : 46.6
                  })()}
                  centerLng={(() => {
                    const c = pros.filter(p => p.latitude && p.longitude)
                    return c.length > 0 ? c.reduce((s, p) => s + parseFloat(p.longitude!), 0) / c.length : 2.3
                  })()}
                  className="h-[400px]"
                />
              </div>
            )}

            {/* Professional cards (list view) */}
            {viewMode === 'list' && isPending && pros.length > 0 && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-xs animate-pulse">
                    <div className="h-14 w-14 shrink-0 rounded-xl bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 w-48 rounded bg-slate-200" />
                      <div className="h-4 w-72 rounded bg-slate-100" />
                      <div className="h-3 w-full rounded bg-slate-100" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(viewMode === 'map' || isPending) ? null : pros.length > 0 ? (
              <div className="space-y-3">
                {pros.map((pro) => (
                  <Link
                    key={pro.id}
                    to={`/pro/${pro.slug}`}
                    className="card-hover flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-xs"
                  >
                    {/* Avatar */}
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-lg font-bold text-primary">
                      {pro.logoUrl ? (
                        <img src={pro.logoUrl} alt={`Logo ${pro.companyName}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      ) : (
                        pro.companyName[0]
                      )}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{pro.companyName}</h3>
                        {pro.identityVerified && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-primary">
                            <CheckCircle className="h-3 w-3" /> Vérifié
                          </span>
                        )}
                        {pro.insuranceVerified && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">
                            <Shield className="h-3 w-3" /> Assuré
                          </span>
                        )}
                      </div>

                      {/* Metrics row */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                        {(pro.reviewCount ?? 0) > 0 ? (
                          <>
                            <div className="flex items-center gap-1">
                              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                              <span className="font-semibold text-slate-900">{formatRating(pro.ratingAvg)}</span>
                              <span className="text-slate-400">({pro.reviewCount} avis)</span>
                            </div>
                            <span className="text-slate-200">|</span>
                          </>
                        ) : (
                          <>
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
                              <Sparkles className="h-3 w-3" /> Nouveau sur la plateforme
                            </span>
                            <span className="text-slate-200">|</span>
                          </>
                        )}
                        <div className="flex items-center gap-1 text-slate-500">
                          <MapPin className="h-3.5 w-3.5" />
                          {pro.city}
                          {pro.departmentCode && <span className="text-slate-400">({pro.departmentCode})</span>}
                        </div>
                        {pro.responseTimeAvgHours && (
                          <>
                            <span className="text-slate-200">|</span>
                            <div className="flex items-center gap-1 text-slate-500">
                              <Clock className="h-3.5 w-3.5" />
                              ~{formatHours(pro.responseTimeAvgHours)}
                            </div>
                          </>
                        )}
                        {pro.yearsExperience && (
                          <>
                            <span className="text-slate-200">|</span>
                            <span className="text-slate-500">{pro.yearsExperience} ans d'expérience</span>
                          </>
                        )}
                        {pro.employeeCount && (
                          <>
                            <span className="text-slate-200">|</span>
                            <div className="flex items-center gap-1 text-slate-500">
                              <Users className="h-3.5 w-3.5" />
                              {pro.employeeCount} {pro.employeeCount > 1 ? 'personnes' : 'personne'}
                            </div>
                          </>
                        )}
                      </div>

                      {pro.shortDescription && (
                        <p className="mt-2 line-clamp-2 text-sm text-slate-500">{pro.shortDescription}</p>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end sm:gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press">
                        Voir le profil <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                      <button
                        type="button"
                        onClick={(e) => toggleCompare(pro.id, e)}
                        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] font-medium transition-all ${
                          compareIds.has(pro.id)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        <span className={`flex h-3.5 w-3.5 items-center justify-center rounded border ${compareIds.has(pro.id) ? 'border-primary bg-primary' : 'border-slate-300'}`}>
                          {compareIds.has(pro.id) && <CheckCircle className="h-3 w-3 text-white" />}
                        </span>
                        Comparer
                      </button>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              /* Empty state */
              <div className="rounded-2xl border-2 border-dashed border-border bg-white p-12 text-center">
                <Search className="mx-auto h-12 w-12 text-slate-300" />
                <h2 className="mt-4 text-lg font-semibold text-slate-900">Aucun professionnel trouvé</h2>
                <p className="mt-2 text-sm text-slate-500">
                  {activeFilterCount > 0
                    ? 'Vos filtres sont peut-être trop restrictifs — essayez d\'en retirer un'
                    : 'Essayez avec d\'autres mots-clés ou ajustez les filtres'}
                </p>

                {/* Suggestions */}
                <div className="mx-auto mt-4 max-w-sm space-y-1.5 text-left">
                  <p className="text-xs font-semibold text-slate-400">Suggestions :</p>
                  <p className="text-xs text-slate-500">• Vérifiez l'orthographe de votre recherche</p>
                  {activeFilterCount > 0 && <p className="text-xs text-slate-500">• Réduisez le nombre de filtres actifs ({activeFilterCount})</p>}
                  <p className="text-xs text-slate-500">• Essayez un terme plus général (ex : "plombier" au lieu de "plombier chauffagiste")</p>
                  <p className="text-xs text-slate-500">• Lancez une demande de devis — nous trouverons les pros pour vous</p>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Effacer les filtres ({activeFilterCount})
                    </button>
                  )}
                  <Link
                    to="/demande-devis/1"
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover btn-press"
                  >
                    Demander un devis gratuit <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            )}

            {/* ── Pagination ──────────────────────────── */}
            {pagination.totalPages > 1 && (
              <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Pagination">
                <button
                  type="button"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-slate-500 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Page précédente"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                  let pageNum: number
                  if (pagination.totalPages <= 7) {
                    pageNum = i + 1
                  } else if (pagination.page <= 4) {
                    pageNum = i + 1
                  } else if (pagination.page >= pagination.totalPages - 3) {
                    pageNum = pagination.totalPages - 6 + i
                  } else {
                    pageNum = pagination.page - 3 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => goToPage(pageNum)}
                      className={`flex h-9 min-w-[36px] items-center justify-center rounded-lg border text-sm font-medium transition-all ${
                        pageNum === pagination.page
                          ? 'border-primary bg-primary text-white shadow-sm'
                          : 'border-border bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      aria-current={pageNum === pagination.page ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  )
                })}

                <button
                  type="button"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-slate-500 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Page suivante"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </nav>
            )}

            {/* ── Inline Quiz ──────────────────────────── */}
            <div className="mt-10">
              <InlineQuiz serviceName="Travaux" cityName="votre ville" categorySlug="renovation" />
            </div>
          </div>

          {/* ── Desktop sticky map (split-view right panel) ── */}
          {pros.length > 0 && (
            <div className="hidden w-[420px] shrink-0 lg:block">
              <div className="sticky top-20">
                <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
                  <ProMap
                    pros={pros}
                    centerLat={(() => {
                      const c = pros.filter(p => p.latitude && p.longitude)
                      return c.length > 0 ? c.reduce((s, p) => s + parseFloat(p.latitude!), 0) / c.length : 46.6
                    })()}
                    centerLng={(() => {
                      const c = pros.filter(p => p.latitude && p.longitude)
                      return c.length > 0 ? c.reduce((s, p) => s + parseFloat(p.longitude!), 0) / c.length : 2.3
                    })()}
                    className="h-[calc(100vh-120px)]"
                  />
                </div>
                <p className="mt-2 text-center text-[10px] text-slate-400">
                  {pros.filter(p => p.latitude && p.longitude).length} professionnels sur la carte
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comparison drawer */}
      {compareIds.size >= 2 && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-white shadow-2xl animate-slide-in">
          <div className="mx-auto max-w-7xl px-4 py-4 lg:px-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Comparer {compareIds.size} professionnels</h3>
              <button type="button" onClick={() => setCompareIds(new Set())} className="text-xs text-slate-500 hover:text-slate-700">Effacer</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 pr-4 text-left text-xs font-medium text-slate-400 w-28"></th>
                    {comparePros.map((p) => (
                      <th key={p.id} className="py-2 px-3 text-left min-w-[160px]">
                        <Link to={`/pro/${p.slug}`} className="text-sm font-semibold text-primary hover:underline">{p.companyName}</Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-b border-slate-100">
                    <td className="py-1.5 pr-4 text-xs text-slate-400">Note</td>
                    {comparePros.map((p) => (
                      <td key={p.id} className="py-1.5 px-3">
                        {(p.reviewCount ?? 0) > 0 ? (
                          <span className="flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{formatRating(p.ratingAvg)} ({p.reviewCount})</span>
                        ) : (
                          <span className="text-xs text-slate-400">Pas encore d'avis</span>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-1.5 pr-4 text-xs text-slate-400">Ville</td>
                    {comparePros.map((p) => <td key={p.id} className="py-1.5 px-3">{p.city}</td>)}
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-1.5 pr-4 text-xs text-slate-400">Réponse</td>
                    {comparePros.map((p) => <td key={p.id} className="py-1.5 px-3">{p.responseTimeAvgHours ? `~${formatHours(p.responseTimeAvgHours)}` : '—'}</td>)}
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-1.5 pr-4 text-xs text-slate-400">Expérience</td>
                    {comparePros.map((p) => <td key={p.id} className="py-1.5 px-3">{p.yearsExperience ? `${p.yearsExperience} ans` : '—'}</td>)}
                  </tr>
                  <tr>
                    <td className="py-1.5 pr-4 text-xs text-slate-400">Vérifié</td>
                    {comparePros.map((p) => (
                      <td key={p.id} className="py-1.5 px-3">
                        {p.identityVerified ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <X className="h-4 w-4 text-slate-300" />}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {compareIds.size >= 2 && <div className="h-52" />}

      <Footer />
    </div>
  )
}
